import { IndicationUnit } from "@shared/components/Metrics/IndicationUnit";
import { linkVariants } from "@shared/components/ui/link";
import { TooltipIcon } from "@solomon-labs/design/components/icons";
import {
    ButtonIcon,
    Tooltip as InfoTooltip,
    Skeleton,
    Text,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
// Aliased: recharts exports its own `Tooltip` and this module uses both.
import React from "react";
import {
    Area,
    ComposedChart,
    Line,
    ResponsiveContainer,
    Tooltip,
    type TooltipContentProps,
    XAxis,
    YAxis
} from "recharts";

import {
    FDIC_SAVINGS_RATE_HISTORY,
    FDIC_SAVINGS_RATE_SOURCE_URL
} from "../data/fdicSavingsRateHistory";
import {
    type ApyComparisonPoint,
    type ApyRateObservation,
    buildApyComparisonSeries,
    rateAtOrBefore,
    startOfUtcYear,
    toFdicRateObservations
} from "../helpers/buildApyComparisonSeries";

const USDV_LINE_COLOR = "rgb(var(--color-accent-base))";
const FDIC_LINE_COLOR = "rgb(var(--color-tint-base))";
// Axis ticks previously reused FDIC_LINE_COLOR; now that the benchmark
// is olive they need their own neutral.
const AXIS_TICK_COLOR = "rgb(var(--color-foreground-muted))";
// The gap band and its label stay neutral. They read across the whole
// plot area, and a 5%-opacity ember wash plus an ember caption would
// spend far more accent than the brand allows for one chart.
const GAP_BAND_COLOR = "rgb(var(--color-foreground-base))";
const GRID_COLOR = "rgb(var(--color-line-base))";
const ACCENT_COLOR = "rgb(var(--color-accent-base))";
const DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
});
const AXIS_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric"
});

const fdicObservations = toFdicRateObservations(FDIC_SAVINGS_RATE_HISTORY);

interface ApyGapChartProps {
    usdvObservations: readonly ApyRateObservation[];
    loading?: boolean;
}

interface ApyGapPoint extends ApyComparisonPoint {
    gapRange?: [number, number];
}

interface ComparisonTooltipProps {
    active?: boolean;
    timestamp?: number;
    usdvObservations: readonly ApyRateObservation[];
}

function formatRate(rate: number | null): string {
    return rate === null ? "—" : `${rate.toFixed(2)}%`;
}

function formatDate(timestamp: number): string {
    return DATE_FORMATTER.format(new Date(timestamp));
}

function formatAxisDate(timestamp: number): string {
    return AXIS_DATE_FORMATTER.format(new Date(timestamp));
}

function ComparisonTooltip({
    active,
    timestamp,
    usdvObservations
}: ComparisonTooltipProps): React.ReactElement | null {
    if (!active || timestamp === undefined) return null;

    const usdvApy = rateAtOrBefore(usdvObservations, timestamp);
    const fdicRate = rateAtOrBefore(fdicObservations, timestamp);

    return (
        /* Surface recipe shared with the Charts components and the
           Earnings tooltip: `surface-inverse-raised` is the surface
           token — `foreground-base` is a *text* token and resolves a
           shade off (#151A25 vs #1B1C20 in light). */
        <div className="flex min-w-48 flex-col gap-2 bg-surface-inverse-raised px-3 py-2">
            <Text variant="small" className="text-foreground-imprint">
                {formatDate(timestamp)}
            </Text>
            <div className="flex items-center justify-between gap-6">
                <Text variant="small" className="text-foreground-inverse">
                    USDv 7-Day APY
                </Text>
                <Text variant="number" className="text-foreground-inverse">
                    {formatRate(usdvApy)}
                </Text>
            </div>
            {/* Both entries share `foreground-inverse` — the benchmark
                row is a peer reading, not secondary detail. Only the
                date header stays dimmed. */}
            <div className="flex items-center justify-between gap-6">
                <Text variant="small" className="text-foreground-inverse">
                    FDIC National Rate
                </Text>
                <Text variant="number" className="text-foreground-inverse">
                    {formatRate(fdicRate)}
                </Text>
            </div>
        </div>
    );
}

/**
 * Linear interpolation keeps the band's top edge on the USDv line at
 * timestamps where the merged series only has an FDIC observation.
 */
/** Side of the square markers, matching the Earnings chart's line ends. */
const DOT_SIZE = 6;

function squareDot(
    fill: string,
    cx: number,
    cy: number,
    key?: string
): React.ReactElement {
    return (
        <rect
            key={key}
            x={cx - DOT_SIZE / 2}
            y={cy - DOT_SIZE / 2}
            width={DOT_SIZE}
            height={DOT_SIZE}
            fill={fill}
        />
    );
}

/**
 * Hover marker — recharts' `activeDot` defaults to a circle, so it has to
 * be overridden explicitly to stay in the IndicationUnit idiom.
 */
function activeDotRenderer(fill: string) {
    return function ActiveDot(props: unknown): React.ReactElement {
        const { cx, cy } = props as { cx?: number; cy?: number };
        if (cx === undefined || cy === undefined) return <g />;
        return squareDot(fill, cx, cy);
    };
}

function usdvRateAt(
    observations: readonly ApyRateObservation[],
    timestamp: number
): number | null {
    if (observations.length === 0) return null;
    if (timestamp < observations[0].timestamp) return null;

    for (let index = 1; index < observations.length; index++) {
        const previous = observations[index - 1];
        const next = observations[index];
        if (timestamp > next.timestamp) continue;

        const span = next.timestamp - previous.timestamp;
        if (span === 0) return next.ratePct;
        const progress = (timestamp - previous.timestamp) / span;
        return previous.ratePct + (next.ratePct - previous.ratePct) * progress;
    }

    return observations[observations.length - 1].ratePct;
}

export function ApyGapChart({
    usdvObservations,
    loading = false
}: ApyGapChartProps): React.ReactElement {
    const sortedUsdvObservations = React.useMemo(
        () => [...usdvObservations].sort((a, b) => a.timestamp - b.timestamp),
        [usdvObservations]
    );
    const endTimestamp = Date.now();
    const startTimestamp = startOfUtcYear(endTimestamp);
    const ytdUsdvObservations = React.useMemo(
        () =>
            sortedUsdvObservations.filter(
                (observation) =>
                    observation.timestamp >= startTimestamp &&
                    observation.timestamp <= endTimestamp
            ),
        [endTimestamp, sortedUsdvObservations, startTimestamp]
    );
    const chartData = React.useMemo((): ApyGapPoint[] => {
        const points = buildApyComparisonSeries(
            ytdUsdvObservations,
            fdicObservations,
            { startTimestamp, endTimestamp }
        );

        return points.map((point) => {
            const usdvApy =
                point.usdvApyPct ??
                usdvRateAt(ytdUsdvObservations, point.timestamp);
            const fdicRate =
                point.fdicSavingsRatePct ??
                rateAtOrBefore(fdicObservations, point.timestamp);

            return {
                ...point,
                fdicSavingsRatePct: fdicRate ?? undefined,
                gapRange:
                    usdvApy !== null && fdicRate !== null
                        ? [fdicRate, usdvApy]
                        : undefined
            };
        });
    }, [endTimestamp, startTimestamp, ytdUsdvObservations]);

    const latestUsdvApy = rateAtOrBefore(ytdUsdvObservations, endTimestamp);
    const latestFdicRate = rateAtOrBefore(fdicObservations, endTimestamp);
    const gapPts =
        latestUsdvApy !== null && latestFdicRate !== null
            ? latestUsdvApy - latestFdicRate
            : null;
    // Stated above the chart rather than floated inside it. The old
    // in-plot label sat at a hardcoded 58% across the time range, so a
    // figure about *today* was drawn at a date where it was not true.
    const gapHeadline = gapPts !== null && gapPts > 0 ? gapPts : null;

    // The crossfade belongs to the skeleton→chart handoff only. On a
    // warm mount (data already cached, e.g. a tab switch) there is
    // nothing to cross from, and fading here would just stack another
    // opacity ramp under the explorer's own entrance.
    const replacedSkeleton = React.useRef(false);
    React.useEffect(() => {
        if (loading) replacedSkeleton.current = true;
    }, [loading]);

    if (loading) {
        // Mirrors the loaded layout line for line — chart box, two
        // legend rows — so the panel doesn't grow when the data lands
        // and shove the rest of the explorer down. Row heights track
        // the `small` variant they stand in for (leading-5 /
        // md:leading-6); the bars sit at glyph height inside them.
        return (
            <div
                key="loading"
                className="flex flex-col gap-4"
                aria-hidden="true"
            >
                <div className="flex h-9 items-center md:h-10">
                    <Skeleton className="h-7 w-40" />
                </div>

                <div className="h-45 w-full" />

                <div className="flex flex-col gap-1">
                    <div className="flex h-5 items-center md:h-6">
                        <Skeleton className="h-3.5 w-48" />
                    </div>
                    <div className="flex h-5 items-center md:h-6">
                        <Skeleton className="h-3.5 w-60" />
                    </div>
                </div>
            </div>
        );
    }

    if (chartData.length === 0) {
        return (
            <div key="empty" className="flex h-45 items-center justify-center">
                <Text variant="body" className="text-foreground-muted">
                    —
                </Text>
            </div>
        );
    }

    return (
        // Keyed apart from the loading branch so the crossfade actually
        // runs on a fresh node. Opacity only — the skeleton already
        // holds the exact final geometry, so there is nothing to slide
        // into place.
        <div
            key="chart"
            data-motion-section
            className="flex flex-col gap-4"
            style={
                replacedSkeleton.current
                    ? {
                          opacity: 0,
                          animation: "fade-in 240ms ease-out forwards"
                      }
                    : undefined
            }
        >
            {gapHeadline !== null && (
                /* Figure at the Earnings headline's scale, with its
                   trailing phrase muted alongside — same shape as
                   "+$0.52 over the next year". Success token and arrow
                   glyph carry over; the arrow steps up to match
                   `number-lg` rather than the `small` line it came
                   from. Only rendered when the gap is positive, so the
                   up arrow is always correct. */
                <div className="flex items-baseline gap-1.5 tabular-nums">
                    <Text
                        variant="number-lg"
                        render={<span />}
                        className="inline-flex items-center gap-0 text-success-base"
                    >
                        <img
                            src="/img/icons/up-short-green.svg"
                            alt=""
                            className="h-7 md:h-8 w-auto"
                            aria-hidden
                        />
                        {gapHeadline.toFixed(2)}%
                    </Text>
                    <Text
                        variant="small"
                        render={<span />}
                        className="text-foreground-muted"
                    >
                        above FDIC
                    </Text>
                </div>
            )}

            <div className="h-45 w-full">
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                    <ComposedChart
                        data={chartData}
                        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                        accessibilityLayer
                    >
                        <XAxis
                            dataKey="timestamp"
                            type="number"
                            scale="time"
                            domain={["dataMin", "dataMax"]}
                            tickFormatter={formatAxisDate}
                            axisLine={false}
                            tickLine={false}
                            minTickGap={48}
                            tick={{
                                fill: AXIS_TICK_COLOR,
                                fontFamily: "ABC Social",
                                fontSize: 12
                            }}
                        />
                        {/* Kept for the domain, hidden for the labels —
                            matching the Earnings chart. The gap headline
                            above states the number, so the scale was
                            spending 42px of plot width to repeat it. */}
                        <YAxis domain={[0, "auto"]} hide />
                        <Tooltip
                            cursor={{
                                stroke: GRID_COLOR,
                                strokeDasharray: "3 3"
                            }}
                            content={(
                                props: TooltipContentProps<number, string>
                            ) => (
                                <ComparisonTooltip
                                    active={props.active}
                                    timestamp={
                                        typeof props.label === "number"
                                            ? props.label
                                            : undefined
                                    }
                                    usdvObservations={ytdUsdvObservations}
                                />
                            )}
                        />
                        <Area
                            type="monotone"
                            dataKey="gapRange"
                            name="Yield gap"
                            stroke="none"
                            fill={GAP_BAND_COLOR}
                            fillOpacity={0.05}
                            activeDot={false}
                            connectNulls
                            isAnimationActive={false}
                            tooltipType="none"
                        />
                        <Line
                            type="stepAfter"
                            dataKey="fdicSavingsRatePct"
                            name="U.S. National Savings Rate (FDIC)"
                            stroke={FDIC_LINE_COLOR}
                            strokeWidth={1.25}
                            strokeDasharray="4 4"
                            dot={false}
                            activeDot={activeDotRenderer(FDIC_LINE_COLOR)}
                            connectNulls
                            isAnimationActive={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="usdvApyPct"
                            name="USDv 7-Day APY"
                            stroke={USDV_LINE_COLOR}
                            strokeWidth={1.5}
                            dot={false}
                            activeDot={activeDotRenderer(ACCENT_COLOR)}
                            connectNulls
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            {/* Legend sits under the chart. The FDIC row carries the
                benchmark disclosure on an info tooltip rather than a
                standing paragraph — it is a definition the reader needs
                once, not every time they read the chart. */}
            <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                    {/* `solid` rather than `top`: the row centres its
                        children, so the dot wants no alignment padding.
                        `pairWith` still sets the size against the text. */}
                    <IndicationUnit
                        variant="solid"
                        pairWith="small"
                        color="accent"
                    />
                    {/* Both legend labels share `foreground-muted` —
                        the dots already carry the series colour, so the
                        text only needs to be readable, not encode which
                        line it belongs to. */}
                    <Text variant="small" className="text-foreground-muted">
                        USDv 7-Day APY, {formatRate(latestUsdvApy)}
                    </Text>
                </div>
                <div className="flex items-center gap-2">
                    <IndicationUnit
                        variant="solid"
                        pairWith="small"
                        color="tint"
                    />
                    {/* Only "FDIC" is the citation — underlining the
                        whole label, rate included, made a legend row
                        read as one big link. String literals rather
                        than bare JSX text so the parenthesis either
                        side of the anchor can't pick up stray
                        whitespace. `linkVariants` inherits colour from
                        the Text, so it stays muted until hover. */}
                    <Text variant="small" className="text-foreground-muted">
                        {"U.S. National Savings Rate ("}
                        <a
                            href={FDIC_SAVINGS_RATE_SOURCE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={linkVariants()}
                        >
                            FDIC
                        </a>
                        {`), ${formatRate(latestFdicRate)}`}
                    </Text>
                    <TooltipProvider delay={0}>
                        <InfoTooltip>
                            <TooltipTrigger
                                render={
                                    <ButtonIcon
                                        variant="transparent"
                                        size="sm"
                                        aria-label="About the FDIC benchmark"
                                        icon={<TooltipIcon />}
                                    />
                                }
                            />
                            <TooltipContent side="top" maxWidth="sm">
                                The FDIC benchmark is the U.S. National Savings
                                Rate. USDv is not a bank deposit or
                                FDIC-insured.
                            </TooltipContent>
                        </InfoTooltip>
                    </TooltipProvider>
                </div>
            </div>
        </div>
    );
}
